import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Lock, Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { useCart, isUnique, maxQty, stockOf } from "../../lib/cart";
import { imgSrc, money, soldOut } from "../../data/artworks";
import { api, apiError } from "../../lib/api";
import { EASE } from "./Reveal";

export const CartDrawer = ({ items }) => {
  const { lines, setQty, remove, open, setOpen } = useCart();
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const rows = lines.map((l) => ({ ...l, a: items.find((x) => x.id === l.id) })).filter((r) => r.a);
  const ok = rows.filter((r) => r.a.prix && !soldOut(r.a));
  const total = ok.reduce((s, r) => s + r.a.prix * (isUnique(r.a) ? 1 : Math.min(r.qty, maxQty(r.a))), 0);

  useEffect(() => {
    if (!open) return undefined;
    setErr("");
    const k = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", k);
    document.documentElement.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", k); document.documentElement.style.overflow = ""; };
  }, [open, setOpen]);

  const checkout = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/checkout", { items: ok.map((r) => ({ artwork_id: r.id, qty: isUnique(r.a) ? 1 : Math.min(r.qty, maxQty(r.a)) })) });
      window.location.assign(data.url);
    } catch (ex) { setErr(apiError(ex)); setBusy(false); }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[75]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-[#0C141B]/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <motion.aside role="dialog" aria-modal="true" aria-label="Panier" data-testid="cart-drawer" data-lenis-prevent
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ duration: 0.55, ease: EASE }}
            className="absolute right-0 top-0 h-full w-full sm:w-[440px] bg-[#F6F1E9] flex flex-col">
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#15202B]/10">
              <h2 className="f-display text-3xl">Votre panier</h2>
              <button onClick={() => setOpen(false)} aria-label="Fermer le panier" data-testid="cart-close-button" className="w-11 h-11 grid place-items-center rounded-full border border-[#15202B]/20"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {rows.length === 0 ? (
                <div className="h-full grid place-items-center text-center" data-testid="cart-empty">
                  <div><ShoppingBag className="w-10 h-10 mx-auto text-[#8A6420]" /><p className="f-display text-2xl mt-4">Votre panier est vide</p>
                    <button onClick={() => setOpen(false)} className="btn-line mt-6">Voir les œuvres</button></div>
                </div>
              ) : rows.map(({ id, qty, a }) => (
                <div key={id} className="grid grid-cols-[72px_minmax(0,1fr)_auto] gap-4 py-4 border-b border-[#15202B]/10" data-testid="cart-line">
                  <img src={imgSrc(a.img)} alt="" className="w-[72px] h-[72px] object-contain bg-[#EFE8DC] p-1" />
                  <div className="min-w-0">
                    <p className="f-display text-xl leading-tight truncate">{a.t}</p>
                    {a.vendu ? <p className="text-sm text-[#A2372F]">Vendue, retirez-la du panier</p>
                      : soldOut(a) ? <p className="text-sm text-[#A2372F]">Épuisé, retirez-le du panier</p>
                      : !a.prix ? <p className="text-sm text-[#A2372F]">Prix à venir</p>
                      : <p className="text-sm text-[#55636F]">{money(a.prix)}{isUnique(a) ? " · pièce unique" : stockOf(a) !== null && stockOf(a) <= 5 ? ` · plus que ${stockOf(a)}` : ""}</p>}
                    {!isUnique(a) && !soldOut(a) && (
                      <div className="mt-2 inline-flex items-center border border-[#15202B]/20 rounded-full">
                        <button onClick={() => setQty(id, qty - 1)} disabled={qty <= 1} aria-label="Moins" className="w-8 h-8 grid place-items-center disabled:opacity-30" data-testid="cart-qty-minus"><Minus className="w-3.5 h-3.5" /></button>
                        <span className="w-6 text-center text-sm tabular-nums" data-testid="cart-qty">{qty}</span>
                        <button onClick={() => setQty(id, qty + 1)} disabled={qty >= maxQty(a)} aria-label="Plus" className="w-8 h-8 grid place-items-center disabled:opacity-30" data-testid="cart-qty-plus"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                  </div>
                  <button onClick={() => remove(id)} aria-label={`Retirer ${a.t}`} className="self-start text-[#A2372F] p-1" data-testid="cart-remove"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            {rows.length > 0 && (
              <div className="border-t border-[#15202B]/10 px-6 py-5 grid gap-3">
                <div className="flex justify-between items-baseline"><span className="text-[#3B4A57]">Total</span><span className="f-display text-3xl tabular-nums" data-testid="cart-total">{money(total)}</span></div>
                <p className="text-xs text-[#55636F]">Adresse de livraison demandée à l'étape suivante. Paiement sécurisé par Square.</p>
                {err && <p className="text-sm text-[#A2372F]" data-testid="cart-error">{err}</p>}
                <button onClick={checkout} disabled={busy || ok.length === 0} className="btn-ink justify-center" data-testid="cart-checkout-button">
                  {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirection…</> : <><Lock className="w-4 h-4" /> Passer au paiement</>}
                </button>
              </div>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
